import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import {
  ArrowLeft, Clock, Calendar, Play, Pause, RefreshCw, Check, X,
  AlertCircle, AlertTriangle, Settings, History, ChevronRight, Timer,
  Globe, RotateCcw, Loader2, Info, Lock, CheckCircle2, Circle
} from 'lucide-react';

const STATUS_LABELS = {
  active: { label: 'Activo', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: Play },
  paused: { label: 'Pausado', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Pause },
  pending_connection: { label: 'Pendiente conexión', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: Clock },
  disabled: { label: 'Deshabilitado', color: 'bg-gray-100 text-gray-500 border-gray-200', icon: X },
};

const EXECUTION_STATUS_LABELS = {
  success: { label: 'Exitoso', color: 'text-emerald-600', icon: CheckCircle2 },
  success_with_warnings: { label: 'Con advertencias', color: 'text-amber-600', icon: AlertTriangle },
  failed: { label: 'Fallido', color: 'text-red-600', icon: AlertCircle },
  running: { label: 'Ejecutando', color: 'text-blue-600', icon: Loader2 },
};

function formatDateTime(isoString) {
  if (!isoString) return '—';
  try {
    const date = new Date(isoString);
    return date.toLocaleString('es-MX', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return isoString.slice(0, 16); }
}

function formatRelativeTime(isoString) {
  if (!isoString) return '—';
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diff = date - now;
    
    if (diff < 0) return 'Pasado';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `En ${days} día${days > 1 ? 's' : ''}`;
    }
    if (hours > 0) return `En ${hours}h ${minutes}m`;
    return `En ${minutes} min`;
  } catch { return '—'; }
}

function ChecklistItem({ done, label }) {
  return (
    <div className={`flex items-center gap-2 text-xs ${done ? 'text-emerald-700' : 'text-muted-foreground'}`}>
      {done ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
      ) : (
        <Circle className="w-4 h-4 text-gray-300" />
      )}
      <span>{label}</span>
    </div>
  );
}

export default function SyncScheduleConfig({ onBack, onViewHistory, onViewErrors }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Form state
  const [frequency, setFrequency] = useState('manual');
  const [timezone, setTimezone] = useState('America/Mexico_City');
  const [retries, setRetries] = useState(2);
  const [retryDelay, setRetryDelay] = useState(15);

  const loadSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/facturacion/schedule');
      setSchedule(res.data);
      setFrequency(res.data.frequency || 'manual');
      setTimezone(res.data.timezone || 'America/Mexico_City');
      setRetries(res.data.retries_on_error ?? 2);
      setRetryDelay(res.data.retry_delay_minutes ?? 15);
    } catch (err) {
      console.error('Error loading schedule:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/facturacion/sync/history', { params: { limit: 10 } });
      setHistory(res.data || []);
    } catch { /* silent */ }
    finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => { 
    loadSchedule(); 
    loadHistory();
  }, [loadSchedule, loadHistory]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/facturacion/schedule', {
        frequency,
        timezone,
        retries_on_error: retries,
        retry_delay_minutes: retryDelay,
      });
      setSchedule(res.data);
      toast.success('Configuración guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handlePause = async () => {
    try {
      const res = await api.post('/facturacion/schedule/pause');
      setSchedule(res.data);
      toast.success('Sincronización pausada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al pausar');
    }
  };

  const handleResume = async () => {
    try {
      const res = await api.post('/facturacion/schedule/resume');
      setSchedule(res.data);
      toast.success('Sincronización reanudada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reanudar');
    }
  };

  const statusInfo = STATUS_LABELS[schedule?.status] || STATUS_LABELS.pending_connection;
  const StatusIcon = statusInfo.icon;
  const canActivate = schedule?.can_activate;
  const isPending = schedule?.status === 'pending_connection';

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40" />
        <Skeleton className="h-60" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="sync-schedule-config">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8" data-testid="schedule-back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Sincronización Programada
          </h2>
          <p className="text-xs text-muted-foreground">
            Configura la frecuencia y parámetros de sincronización automática
          </p>
        </div>
        <Badge className={`border ${statusInfo.color}`} variant="outline">
          <StatusIcon className="w-3 h-3 mr-1" />
          {statusInfo.label}
        </Badge>
      </div>

      {/* Pending Connection Notice */}
      {isPending && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-800 text-sm">Pendiente de conexión Google</p>
              <p className="text-xs text-amber-700 mt-1">
                La sincronización automática se activará después de completar los siguientes pasos:
              </p>
              <div className="mt-3 space-y-1.5 bg-white/50 rounded-lg p-3">
                {(schedule?.activation_checklist || []).map((item, i) => (
                  <ChecklistItem key={i} done={item.done} label={item.label} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Configuration Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Settings className="w-4 h-4" /> Configuración de frecuencia
          </CardTitle>
          <CardDescription className="text-xs">
            Define cada cuánto tiempo se sincronizarán automáticamente los datos
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Frequency */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Frecuencia de sincronización</Label>
            <Select value={frequency} onValueChange={setFrequency} disabled={isPending}>
              <SelectTrigger className="h-9" data-testid="frequency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper">
                {schedule?.frequencies && Object.entries(schedule.frequencies).map(([key, freq]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <span>{freq.label}</span>
                      {freq.description && (
                        <span className="text-[10px] text-muted-foreground">({freq.description})</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {frequency !== 'manual' && !canActivate && (
              <p className="text-[10px] text-amber-600 flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Requiere completar la configuración de Google OAuth primero
              </p>
            )}
          </div>

          {/* Timezone */}
          <div className="space-y-2">
            <Label className="text-xs font-medium flex items-center gap-1">
              <Globe className="w-3 h-3" /> Zona horaria
            </Label>
            <Select value={timezone} onValueChange={setTimezone} disabled={isPending}>
              <SelectTrigger className="h-9" data-testid="timezone-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper">
                {(schedule?.timezones || []).map(tz => (
                  <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Retry Configuration */}
          <div className="space-y-3">
            <Label className="text-xs font-medium flex items-center gap-1">
              <RotateCcw className="w-3 h-3" /> Reintentos en caso de error
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">Número de reintentos</Label>
                <Input 
                  type="number" 
                  value={retries} 
                  onChange={e => setRetries(Math.max(0, Math.min(5, parseInt(e.target.value) || 0)))}
                  className="h-8 text-xs"
                  min={0}
                  max={5}
                  disabled={isPending}
                  data-testid="retries-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">Espera entre reintentos (min)</Label>
                <Input 
                  type="number" 
                  value={retryDelay} 
                  onChange={e => setRetryDelay(Math.max(5, Math.min(60, parseInt(e.target.value) || 15)))}
                  className="h-8 text-xs"
                  min={5}
                  max={60}
                  disabled={isPending}
                  data-testid="retry-delay-input"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Next / Last Execution */}
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="space-y-1">
              <span className="text-muted-foreground">Próxima ejecución</span>
              {schedule?.next_execution ? (
                <div>
                  <p className="font-medium">{formatDateTime(schedule.next_execution)}</p>
                  <p className="text-[10px] text-muted-foreground">{formatRelativeTime(schedule.next_execution)}</p>
                </div>
              ) : (
                <p className="text-muted-foreground">—</p>
              )}
            </div>
            <div className="space-y-1">
              <span className="text-muted-foreground">Última ejecución</span>
              {schedule?.last_execution ? (
                <div>
                  <p className="font-medium">{formatDateTime(schedule.last_execution)}</p>
                  {schedule.last_execution_status && (
                    <Badge variant="outline" className={`text-[10px] ${EXECUTION_STATUS_LABELS[schedule.last_execution_status]?.color || ''}`}>
                      {EXECUTION_STATUS_LABELS[schedule.last_execution_status]?.label || schedule.last_execution_status}
                    </Badge>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground">Nunca ejecutado</p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            {schedule?.status === 'active' ? (
              <Button variant="outline" onClick={handlePause} className="flex-1 gap-1.5" data-testid="pause-btn">
                <Pause className="w-3.5 h-3.5" /> Pausar
              </Button>
            ) : schedule?.status === 'paused' ? (
              <Button variant="outline" onClick={handleResume} className="flex-1 gap-1.5" data-testid="resume-btn">
                <Play className="w-3.5 h-3.5" /> Reanudar
              </Button>
            ) : null}
            <Button onClick={handleSave} disabled={saving || isPending} className="flex-1 gap-1.5" data-testid="save-schedule-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Guardar configuración
            </Button>
          </div>

          {/* Blocked message */}
          {isPending && (
            <p className="text-[10px] text-center text-muted-foreground">
              La sincronización programada se habilitará después de conectar Google Sheets y validar la primera sincronización manual.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Sync History Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm flex items-center gap-2">
                <History className="w-4 h-4" /> Historial de sincronizaciones
              </CardTitle>
              <CardDescription className="text-xs">
                Últimas ejecuciones y su estado
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={loadHistory} disabled={historyLoading} className="gap-1 text-xs">
              <RefreshCw className={`w-3 h-3 ${historyLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {historyLoading && !history.length ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div>
          ) : history.length > 0 ? (
            <div className="space-y-2">
              {history.map((item, i) => {
                const statusInfo = EXECUTION_STATUS_LABELS[item.status] || {};
                const StatusIcon = statusInfo.icon || Circle;
                return (
                  <div key={item.id || i} 
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <StatusIcon className={`w-4 h-4 ${statusInfo.color || 'text-gray-400'} ${item.status === 'running' ? 'animate-spin' : ''}`} />
                      <div>
                        <p className="text-xs font-medium">{formatDateTime(item.started_at)}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {item.records_processed || 0} procesados · 
                          {item.records_created || 0} nuevos · 
                          {item.errors_count || 0} errores
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] ${statusInfo.color || ''}`}>
                        {statusInfo.label || item.status}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        {item.duration_seconds ? `${Math.round(item.duration_seconds)}s` : '—'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <History className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-xs">No hay historial de sincronizaciones</p>
              <p className="text-[10px]">El historial aparecerá después de la primera sincronización</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="flex gap-2">
        {onViewErrors && (
          <Button variant="outline" onClick={onViewErrors} className="flex-1 gap-1.5 text-xs" data-testid="view-errors-btn">
            <AlertTriangle className="w-3.5 h-3.5" /> Ver errores
          </Button>
        )}
      </div>
    </div>
  );
}
